import { v4 as generateUUID } from 'uuid';
import { HamiltonProductList } from '../building-blocks/wish-list/wish-list.module';
import { getProductDetail, HamiltonProduct } from './products-api';

export const ANONYMOUS_WISHLIST_STORAGE_KEY = 'hamilton_anonymous_wishlists';

export const isUserLoggedIn = (): boolean => {
  const html = document.querySelector('html');
  if (!html) {
    return false;
  }
  return !html.classList.contains('user-logged-out') && !!html.dataset.uid;
};

export const getAnonymousWishlists = (): HamiltonProductList[] => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return [];
  }

  try {
    const rawData = window.localStorage.getItem(ANONYMOUS_WISHLIST_STORAGE_KEY);
    if (!rawData) {
      return [];
    }
    const parsed = JSON.parse(rawData);
    if (Array.isArray(parsed)) {
      return parsed;
    }
    return [];
  } catch (error) {
    console.error('Error reading anonymous wishlists from localStorage:', error);
    return [];
  }
};

export const saveAnonymousWishlists = (wishlists: HamiltonProductList[]): boolean => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }

  try {
    window.localStorage.setItem(ANONYMOUS_WISHLIST_STORAGE_KEY, JSON.stringify(wishlists));
    return true;
  } catch (error) {
    console.error('Error saving anonymous wishlists to localStorage:', error);
    return false;
  }
};

export const createAnonymousWishlist = (wishlistName: string): Promise<boolean> => {
  const listId = generateUUID();
  const newWishlist: HamiltonProductList = {
    '@name': listId,
    '@id': listId,
    name: wishlistName,
    'mgnl:lastModified': new Date().toISOString(),
    items: [],
    articles: []
  };

  const wishlists = getAnonymousWishlists();
  wishlists.push(newWishlist);
  const success = saveAnonymousWishlists(wishlists);
  return Promise.resolve(success);
};

export const renameAnonymousWishlist = (wishlistId: string, newWishlistName: string): Promise<boolean> => {
  const wishlists = getAnonymousWishlists();
  const wishlist = wishlists.find((list) => list['@name'] === wishlistId || list['@id'] === wishlistId);

  if (!wishlist) {
    return Promise.resolve(false);
  }

  wishlist.name = newWishlistName;
  wishlist['mgnl:lastModified'] = new Date().toISOString();
  const success = saveAnonymousWishlists(wishlists);
  return Promise.resolve(success);
};

export const deleteAnonymousWishlist = (wishlistId: string): Promise<boolean> => {
  const wishlists = getAnonymousWishlists();
  const filtered = wishlists.filter((list) => list['@name'] !== wishlistId && list['@id'] !== wishlistId);

  if (filtered.length === wishlists.length) {
    return Promise.resolve(false);
  }

  if (typeof window !== 'undefined' && window.localStorage) {
    if (window.localStorage.getItem('defaultWishlist') === wishlistId) {
      window.localStorage.removeItem('defaultWishlist');
    }
  }

  const success = saveAnonymousWishlists(filtered);
  return Promise.resolve(success);
};

export const addItemToAnonymousWishlist = async (
  productId: string,
  wishListId: string
): Promise<boolean> => {
  const wishlists = getAnonymousWishlists();
  const wishlist = wishlists.find((list) => list['@name'] === wishListId || list['@id'] === wishListId);

  if (!wishlist) {
    return false;
  }

  if (!wishlist.items) {
    wishlist.items = [];
  }

  const alreadyInList = wishlist.items.some(
    (item) => item['@id'] === productId || (item as any).nodeid === productId
  );

  if (alreadyInList) {
    return true;
  }

  try {
    const productDetail = await getProductDetail(productId);
    if (productDetail) {
      const productItem: HamiltonProduct = {
        ...productDetail,
        '@id': productDetail['@id'] || productId
      };
      wishlist.items.push(productItem);
    } else {
      wishlist.items.push({
        '@id': productId,
        partNumber: '',
        name: '',
        description: '',
        packagingUnit: '',
        image: { renditions: { small: { link: '' }, large: { link: '' } } },
        taxonomy: { 'Patient-groups': [], Use: [] },
        patientGroups: '',
        typeOfUse: '',
        icons: []
      });
    }
  } catch {
    wishlist.items.push({
      '@id': productId,
      partNumber: '',
      name: '',
      description: '',
      packagingUnit: '',
      image: { renditions: { small: { link: '' }, large: { link: '' } } },
      taxonomy: { 'Patient-groups': [], Use: [] },
      patientGroups: '',
      typeOfUse: '',
      icons: []
    });
  }

  wishlist['mgnl:lastModified'] = new Date().toISOString();
  return saveAnonymousWishlists(wishlists);
};

export const deleteItemFromAnonymousWishlist = (
  productId: string,
  wishListId: string
): Promise<boolean> => {
  const wishlists = getAnonymousWishlists();
  const wishlist = wishlists.find((list) => list['@name'] === wishListId || list['@id'] === wishListId);

  if (!wishlist || !wishlist.items) {
    return Promise.resolve(false);
  }

  const initialLength = wishlist.items.length;
  wishlist.items = wishlist.items.filter(
    (item) => item['@id'] !== productId && (item as any).nodeid !== productId
  );

  if (wishlist.items.length === initialLength) {
    return Promise.resolve(false);
  }

  wishlist['mgnl:lastModified'] = new Date().toISOString();
  const success = saveAnonymousWishlists(wishlists);
  return Promise.resolve(success);
};
